import clsx from "clsx";
import { RegisterOptions, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import Toggle from "@/app/components/ui/Toggles";
import { useRouter } from "@/app/hooks/useRouter";
import { useSearchParams } from "react-router-dom";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import Input from "@/app/components/ui/Inputs/Input";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { IGroomerStaffCreateBody } from "@/api_framework/api_modals/Configuration";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import { useConfigurationGroomerStaffCreateAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationGroomerStaffCreateAPI";
import { useConfigurationGroomerStaffDetailAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationGroomerStaffDetailAPI";

type GroomerType = "bath" | "haircut" | "contractual_bath" | "contractual_haircut";
type GroomerStatus = "active" | "inactive";

const GroomerOnboardComponent = () => {
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { execute } = useConfigurationGroomerStaffCreateAPI();
  const { execute: fetchDetail } = useConfigurationGroomerStaffDetailAPI();
  const [status, setStatus] = useState<GroomerStatus>("active");
  const [selectedType, setSelectedType] = useState<GroomerType>("bath");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mappedClinicId } = useAppSelector((state) => state.root.UIStates);
  const editGroomerId = params.get("groomer_id") || undefined;
  const { cities } = useRouteInfo("INVENTORY")?.routeState?.state as IInventoryState;
  const fetchCities = useOrgCitiesListAPI("INVENTORY");
  const { forEmail, forOnlyNumbers, forOnlyAlphabetWithoutDotSpace } = useFormValidations();
  const userPermissions = useAppSelector((state) => state?.auth?.userPermissions);
  const [contractualItem, setContractualItem] = useState<any>([]);
  const cityOrgItem = useSelectMenuReducer(cities, "name", "id");

  useEffect(() => {
    const actions = userPermissions?.role_permission?.actions || [];
    const items = [];
    if (actions.includes("NON_CONTRACTUAL_GROOMER")) {
      items.push({ label: "Bath", value: "bath", icon: "🛁" }, { label: "Haircut", value: "haircut", icon: "✂️" });
    }
    if (actions.includes("CONTRACTUAL_GROOMER")) {
      items.push(
        { label: "Contractual Bath", value: "contractual_bath", icon: "👤" },
        { label: "Contractual Haircut", value: "contractual_haircut", icon: "👤" },
      );
    }
    setContractualItem(items);
  }, [userPermissions]);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<IGroomerStaffCreateBody>({
    mode: "onChange",
    defaultValues: {
      name: "",
      phone_number: "",
      email: "",
      groomer_type: "bath",
      status: "active",
      employee_id: "",
      city_id: "",
      city_name: "",
      truein_identifier: "",
      groomer_id: editGroomerId,
    },
  });

  useEffect(() => {
    if (!editGroomerId) return;
    let active = true;
    fetchDetail(editGroomerId).then((data) => {
      if (!active) return;
      if (!data) return;
      setValue("name", data.name || "");
      setValue("phone_number", data.phone_number || "");
      setValue("email", data.email || "");
      setValue("employee_id", data.employee_id || "");
      setValue("truein_identifier", data.truein_identifier || "");
      setValue("city_id", data.city_id || "", { shouldValidate: true });
      setValue("city_name", data.city_name || "", { shouldValidate: true });
      setStatus((data.status?.toLowerCase() as GroomerStatus) || "active");
      setSelectedType((data.groomer_type?.toLowerCase() as GroomerType) || "bath");
    });
    return () => {
      active = false;
    };
  }, [editGroomerId, fetchDetail, setValue]);

  const onSubmit = async (data: IGroomerStaffCreateBody) => {
    setIsSubmitting(true);
    try {
      await execute({
        ...data,
        status,
        groomer_type: selectedType,
        city_id: data.city_id,
        groomer_id: editGroomerId,
        clinic_id: ["contractual_bath", "contractual_haircut"].includes(selectedType) ? mappedClinicId : undefined,
      });
      navigate(getRouteKey("GROOMER_SETUP", "url"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="px-5 py-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-medium text-[#000000]">Groomer Profile</h1>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-700">Status</span>
          <Toggle
            checked={status === "active"}
            onToggle={() => setStatus((prev) => (prev === "active" ? "inactive" : "active"))}
          />
        </div>
      </div>
      <div className="rounded-2xl border border-slate-100 bg-white mt-10 shadow-sm">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="rounded-2xl bg-slate-50 p-4">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm font-medium text-[#000000]">
                Personal Details <span className="text-rose-500">*</span>
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[#000000]">Groomer Name</label>
                <Input
                  className="text-xs"
                  placeholder="Enter groomer name"
                  register={register("name", {
                    ...((forOnlyAlphabetWithoutDotSpace?.validations as RegisterOptions<
                      IGroomerStaffCreateBody,
                      "name"
                    >) || {}),
                    required: true,
                  })}
                  fieldError={errors.name}
                  errorMessages={[
                    forOnlyAlphabetWithoutDotSpace.errors,
                    { message: errors.name?.message, type: "manual" },
                    { message: "", type: "required" },
                  ]}
                  inputClassName="text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[#000000]">Phone Number</label>
                <Input
                  className="text-xs"
                  placeholder="1234 1234 1234"
                  maxLength={10}
                  inputClassName="text-sm"
                  register={register("phone_number", {
                    ...((forOnlyNumbers?.validations as RegisterOptions<IGroomerStaffCreateBody, "phone_number">) ||
                      {}),
                    required: true,
                  })}
                  fieldError={errors.phone_number}
                  errorMessages={[
                    forOnlyNumbers.errors,
                    { message: errors.phone_number?.message, type: "manual" },
                    { message: "", type: "required" },
                  ]}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[#000000]">Email</label>
                <Input
                  className="text-xs"
                  placeholder="name@example.com"
                  inputClassName="text-sm"
                  register={register("email", {
                    ...((forEmail?.validations as RegisterOptions<IGroomerStaffCreateBody, "email">) || {}),
                    required: true,
                  })}
                  fieldError={errors.email}
                  errorMessages={[
                    forEmail.errors,
                    { message: errors.email?.message, type: "manual" },
                    { message: "", type: "required" },
                  ]}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[#000000]">City</label>
                <NonSearchableSelectMenu
                  errorMessages={[{ message: "", type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      setValue("city_id", item.id, { shouldValidate: true });
                      setValue("city_name", item.title || "", { shouldValidate: true });
                    }
                  }}
                  fieldError={errors?.city_id || null}
                  register={register("city_id", { required: true })}
                  selectItems={cityOrgItem}
                  placeholder="Select City"
                  showTooltips={false}
                  showDropdownIcon={true}
                  defaultSelected={cityOrgItem.find((c) => c.id === watch("city_id"))}
                  listBoxClassName="w-full"
                  className={clsx("text-gray-800")}
                  containerClassName="w-full"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[#000000]">Employee id</label>
                <Input
                  className="text-xs"
                  placeholder="Enter Truein Identifier"
                  inputClassName="text-sm"
                  register={register("employee_id", {
                    required: false,
                  })}
                  fieldError={errors.employee_id}
                  errorMessages={[
                    { message: errors.employee_id?.message, type: "manual" },
                    { message: "", type: "required" },
                  ]}
                />
              </div>
              <div className="flex flex-col gap-1 md:col-span-2">
                <label className="text-xs font-medium text-[#000000]">Groomer Type</label>
                <div className="grid gap-3 sm:grid-cols-4">
                  {contractualItem?.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setSelectedType(option.value)}
                      className={clsx(
                        "flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition",
                        selectedType === option.value
                          ? "border-rose-300 bg-rose-50 text-rose-700"
                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50",
                      )}
                    >
                      <span>{option.icon}</span>
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6 flex items-center justify-end gap-3 mr-5 mb-3">
            <button
              type="button"
              onClick={() => navigate(getRouteKey("GROOMER_SETUP", "url"))}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={clsx(
                "rounded-lg px-6 py-2 text-sm font-medium text-white shadow-sm",
                isSubmitting ? "bg-slate-400 cursor-not-allowed" : "bg-black hover:bg-slate-900",
              )}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GroomerOnboardComponent;
